import React, { useMemo, useState, useCallback, useEffect, useRef } from "react";
import { MobileOutlined, TabletOutlined, LaptopOutlined, DesktopOutlined } from "@ant-design/icons";
import { SpacingField } from "./SpacingField";
import { typography, textColors } from "../../../design-system/tokens";
import InfoIcon from "../InfoIcon";
import ScreenSizeModeSelector from "./ScreenSizeModeSelector";

function deriveTwoMode(values) {
  const xs = values?.extra_small;
  const s = values?.small ?? xs;
  const m = values?.medium;
  const l = values?.large ?? m;
  const xl = values?.extra_large ?? l;
  const xsEqual = xs && s && xs.top === s.top && xs.right === s.right && xs.bottom === s.bottom && xs.left === s.left;
  const mEqual = m && l && xl && m.top === l.top && l.top === xl.top && m.right === l.right && l.right === xl.right && m.bottom === l.bottom && l.bottom === xl.bottom && m.left === l.left && l.left === xl.left;
  return { isTwo: Boolean(xsEqual && mEqual), vMobile: xs || s || m, vDesktop: m || l || xl };
}

function allEqual(values) {
  const keys = ["extra_small", "small", "medium", "large", "extra_large"];
  const first = values?.extra_small;
  if (!first) return false;
  return keys.every((k) => {
    const v = values?.[k];
    return v && v.top === first.top && v.right === first.right && v.bottom === first.bottom && v.left === first.left;
  });
}

export default function ByLayoutSpacingField({ label, help, value, onChange, field, infoText }) {
  const { isTwo, vMobile, vDesktop } = useMemo(() => deriveTwoMode(value || {}), [value]);
  const [mode, setMode] = useState(allEqual(value || {}) ? "one" : isTwo ? "two" : "all");
  const modeTouchedRef = useRef(false);

  // If the value arrives after the first render (common with async metadata/selection),
  // auto-select the most compact mode as long as the user hasn't manually chosen a mode.
  useEffect(() => {
    if (modeTouchedRef.current) return;
    const next = allEqual(value || {}) ? "one" : deriveTwoMode(value || {}).isTwo ? "two" : "all";
    setMode(next);
  }, [value]);

  // Capitalize first character of label
  const formattedLabel = typeof label === "string" && label.length > 0
    ? label.charAt(0).toUpperCase() + label.slice(1)
    : label;

  const setUnified = useCallback((spacing) => {
    onChange({
      extra_small: spacing,
      small: spacing,
      medium: spacing,
      large: spacing,
      extra_large: spacing,
    });
    setMode("one");
  }, [onChange]);

  const applyMobile = useCallback((spacing) => {
    onChange({ extra_small: spacing, small: spacing });
    setMode("two");
  }, [onChange]);

  const applyDesktop = useCallback((spacing) => {
    onChange({ medium: spacing, large: spacing, extra_large: spacing });
    setMode("two");
  }, [onChange]);

  return (
    <div className="field-wrapper">
      {label && (
        <label className={`block mb-2 ${typography.label} ${textColors.secondary}`}>
          {formattedLabel}
          {infoText ? <span className="ml-2 align-middle"><InfoIcon text={infoText} /></span> : null}
        </label>
      )}
      {help && <div className={`mb-2 ${typography.caption} ${textColors.tertiary}`}>{help}</div>}
      <ScreenSizeModeSelector
        mode={mode}
        onChange={(newMode) => {
          modeTouchedRef.current = true;
          setMode(newMode);
        }}
        disabled={false}
      />

      {mode === "one" ? (
        <SpacingField
          label=""
          help={null}
          value={value?.extra_small || value?.small || value?.medium || value?.large || value?.extra_large}
          onChange={setUnified}
          field={field}
          labelSize="small"
        />
      ) : mode === "two" ? (
        <div className="grid grid-cols-1 gap-2">
          <SpacingField label="Phone" help={null} value={vMobile} onChange={applyMobile} field={field} labelSize="small" />
          <SpacingField label="Desktop" help={null} value={vDesktop} onChange={applyDesktop} field={field} labelSize="small" />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-2">
          <SpacingField label="Phone" help={null} value={value?.extra_small} onChange={(v) => onChange({ extra_small: v })} field={field} labelSize="small" />
          <SpacingField label="Tablet" help={null} value={value?.small || value?.extra_small} onChange={(v) => onChange({ small: v })} field={field} labelSize="small" />
          <SpacingField label="Laptop" help={null} value={value?.medium} onChange={(v) => onChange({ medium: v })} field={field} labelSize="small" />
          <SpacingField label="Desktop" help={null} value={value?.large || value?.medium} onChange={(v) => onChange({ large: v })} field={field} labelSize="small" />
          <SpacingField label="Desk XL" help={null} value={value?.extra_large || value?.large} onChange={(v) => onChange({ extra_large: v })} field={field} labelSize="small" />
        </div>
      )}
    </div>
  );
}


